import CloseIcon from '@mui/icons-material/Close';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import {LoadingButton} from '@mui/lab';
import {Box, Button, Divider, IconButton, Tooltip} from '@mui/material';
import {useSnackbar} from 'notistack';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useState,
} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {saveFile} from '../../../utils/file';
import AMSExportFieldForm from '../../common/AMSExportFieldForm';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import ExportPdfButton from '../../common/ExportPdfButton';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';
import {GasMonitoringReportData} from '../ConnectView/GasMonitoringReport';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  value?: GasMonitoringReportData;
}

const AMSImportExportButton = <T extends ComponentType>({
  component,
  componentProps,
  children,
  value,
}: Props<T>) => {
  const Component = component ?? Button;
  const {enqueueSnackbar} = useSnackbar();
  const [isFetching, setIsFetching] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [isCsvModalOpened, setIsCsvModalOpened] = useState(false);

  const fetchData = async (fields: string[]) => {
    setIsCsvModalOpened(false);
    setIsFetching(true);
    try {
      const resp = await API.get(`${apiBaseUrl}/ams`, {
        params: {
          ...value?.params,
          export: 'excel',
          fields,
        },
        responseType: 'blob',
      });
      const filename =
        resp?.headers['content-disposition']?.split('filename=')?.[1] ||
        'ams_node_list.xlsx';
      saveFile(resp.data, filename);
    } catch (error: unknown) {
      const messages = getMessagesFromApiError(error, 'STRA_768b6bc8');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsFetching(false);
  };

  return (
    <>
      <Tooltip title="Export">
        <Component {...componentProps} onClick={() => setIsOpened(true)}>
          {children}
        </Component>
      </Tooltip>

      {isOpened ? (
        <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            gap={3}
            p={3}
          >
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                <ImportExportIcon sx={{color: 'primary.main'}} />
                <Box fontSize={24}>Export Sensors</Box>
              </Box>
              <IconButton onClick={() => setIsOpened(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              gap={3}
            >
              <Divider>Export</Divider>
              <Box display="flex" flexDirection="column" gap={2}>
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  loading={isFetching}
                  onClick={() => setIsCsvModalOpened(true)}
                >
                  Export to CSV
                </LoadingButton>
              </Box>
              <ExportPdfButton
                endpoint="/ams"
                defaultFilename="ams_sensor_list.pdf"
                params={value?.params}
                exportFields={value?.exportFields}
              />
            </Box>
          </Box>
        </ModalFixed>
      ) : null}

      <ModalFixed open={isCsvModalOpened} onClose={() => setIsCsvModalOpened(false)}>
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <AMSExportFieldForm
            exportFields={value?.exportFields}
            loading={isFetching}
            onChange={fetchData}
            close={() => setIsCsvModalOpened(false)}
          />
        </Box>
      </ModalFixed>
    </>
  );
};

export default AMSImportExportButton;
